import React, { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useSnackbar } from "notistack";
import ReactConfetti from "react-confetti";
import {
  FormBaseType,
  FormBasicsDto,
  FormSectionDto,
} from "@green0meter/api-client";
import { FieldValues } from "react-hook-form";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useCreateSubmission, useGetAvailableForms } from "@api";
import { MultistepForm, WelcomeBanner } from "@components/common";
import { Box, CircularProgress } from "@components/base";
import { useUserStore } from "@store/user";

type SubmissionMode = null | "save" | "submit";

const SupplierForm: FC = () => {
  const { procurementId, formId } = useParams<{
    procurementId: string;
    formId: string;
  }>();
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formBasics, setFormBasics] = useState<FormBasicsDto>();
  const [submissionMode, setSubmissionMode] = useState<SubmissionMode>(null);

  const {
    createSubmissionAsync,
    createSubmissionEnabled,
    isPendingCreateSubmission,
    createSubmissionError,
  } = useCreateSubmission(user);
  const { availableForms, isFetchingAvailableForms, isSuccessAvailableForms } =
    useGetAvailableForms(user, [FormBaseType.ProcurementSupplier], procurementId);
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const submitForm = async (
    values: FieldValues,
    sections: FormSectionDto[],
  ): Promise<void> => {
    const isValid = sections.every((section) =>
      section.formQuestions?.every((question) => {
        if (question.isRequired) {
          const value = values[question.code];

          return value !== undefined && value !== "";
        }

        return true;
      }),
    );

    if (formId && isValid) {
      await createSubmissionAsync({
        formId,
      });

      setSubmissionMode("submit");
      setFormSubmitted(true);
    }
  };

  const handleSubmit = (
    values: FieldValues,
    sections: FormSectionDto[],
  ): Promise<void> => submitForm(values, sections);

  const handleGoBack = (): void => {
    setSubmissionMode("save");
    setFormSubmitted(true);
  };

  useEffect(() => {
    if (isSuccessAvailableForms && availableForms) {
      setFormBasics(availableForms?.find((form) => form.id === formId));
    }
  }, [formId, availableForms, isSuccessAvailableForms]);

  useEffect(() => {
    const errorData = (createSubmissionError as AxiosError)?.response?.data as {
      message: string;
    };

    if (errorData) {
      enqueueSnackbar(errorData.message, {
        variant: "error",
      });
    }
  }, [createSubmissionError]);

  const welcomeBannerText =
    submissionMode === "save"
      ? {
          text: t("procurement.form.submit.formSaved"),
        }
      : {
          text: t("procurement.form.submit.dataSubmittedAndCalculationStarted"),
        };

  const now = dayjs();
  const deadline = formBasics?.deadline ? dayjs(formBasics.deadline) : null;
  const isDeadlinePassed = deadline ? now.isAfter(deadline, "day") : false;
  const isLocked = isDeadlinePassed ? true : !!formBasics?.isLocked;

  // TODO: move thank you banner to co2 hub? -> after submission route back to co2 hub?
  return formSubmitted ? (
    <Box p={3}>
      <WelcomeBanner
        title={t("common.thankYou")}
        text={welcomeBannerText.text}
        onClose={() => navigate(`/suppliers-hub/${procurementId}`)}
        color="blue"
        icon="data-cloud"
      />
      <ReactConfetti recycle={false} />
    </Box>
  ) : formId && isFetchingAvailableForms ? (
    <CircularProgress />
  ) : formBasics ? (
    <MultistepForm
      formSettings={{
        formBasics: {
          ...formBasics,
          isLocked: isLocked,
        },
        button: createSubmissionEnabled
          ? {
              label: t("form.supplier.submitButtonLabel"),
              onClick: (): void => {},
              popoverButtons: [
                {
                  title: t("form.supplier.submitCalculate.title"),
                  description: t("form.supplier.submitCalculate.description"),
                  disableSubmit: true,
                  onClick: handleGoBack,
                },
                {
                  title: t("form.supplier.submitCreateReports.title"),
                  description: t("form.supplier.submitCreateReports.description"),
                  onClick: handleSubmit,
                },
              ],
            }
          : undefined,
      }}
      isSubmissionPending={isPendingCreateSubmission}
    />
  ) : (
    <Typography>{t("csob.supplier.hub.supplierNotFound")}</Typography>
  );
};

export default SupplierForm;
